import { onMounted, onUnmounted } from 'vue'
import logger from '../utils/logger'

/**
 * Composable to automatically reload data when token is refreshed
 * Use this in components that load data to ensure they refresh after token expiration
 * 
 * @param reloadFn - Function to call when token is refreshed
 * @param delay - Delay in milliseconds before reloading (default: 100ms)
 * 
 * @example
 * ```ts
 * const loadData = async () => { ... }
 * useTokenRefreshReload(() => loadData())
 * ```
 */
export function useTokenRefreshReload(reloadFn: () => void | Promise<void>, delay: number = 100) {
  const handleTokenRefresh = (event: CustomEvent) => {
    if (event.detail?.success) {
      logger.debug('Token refreshed - reloading data')
      // Small delay to ensure cookies are set and token is ready
      setTimeout(() => {
        reloadFn()
      }, delay)
    }
  }

  onMounted(() => {
    window.addEventListener('token-refreshed', handleTokenRefresh as EventListener)
  })

  onUnmounted(() => {
    window.removeEventListener('token-refreshed', handleTokenRefresh as EventListener)
  })
}

